import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatDateTime, formatRelative } from '@/lib/dates'

/**
 * Platform-admin Alerts triage — every user-reported "Report a problem"
 * lands here for Mira / Emil to work through.
 *
 * v1: status filter (open / in_progress / resolved / dismissed / all)
 * + cross-tenant list ordered newest-first. Click into the detail
 * panel to write triage notes, change status, and see the assistant_
 * requests trail captured at report time. Auto-refresh every 15s while
 * the page is open so new reports surface without a manual reload.
 */

type AlertStatus = 'open' | 'in_progress' | 'resolved' | 'dismissed'

type RelatedRequest = {
  id: string
  channel?: string
  request_text?: string
  final_reply?: string | null
  created_at?: string
}

type AlertEntry = {
  id: string
  shop_id: string
  shop_name: string | null
  reporter_clerk_user_id: string
  reporter_email: string | null
  description: string
  page_context: string | null
  status: AlertStatus
  triage_notes: string | null
  resolved_at: string | null
  resolved_by_clerk_user_id: string | null
  created_at: string
  updated_at: string
  related: RelatedRequest[]
}

type Resp = { entries: AlertEntry[] }

const STATUS_LABEL: Record<AlertStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
}

const STATUS_TINT: Record<AlertStatus, string> = {
  open: 'bg-red-100 text-red-800 border-red-200',
  in_progress: 'bg-amber-100 text-amber-800 border-amber-200',
  resolved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  dismissed: 'bg-slate-200 text-slate-700 border-slate-300',
}

export function AdminAlerts() {
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<AlertStatus | 'all'>('open')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const query = useQuery<Resp>({
    queryKey: ['admin', 'alerts', statusFilter],
    enabled: isLoaded,
    refetchInterval: 15_000,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const qs = statusFilter === 'all' ? '' : `?status=${statusFilter}`
      const res = await fetch(`/api/admin/alerts${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Resp
    },
  })

  const selected = query.data?.entries.find((e) => e.id === selectedId) ?? null

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Alerts
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          User-reported issues from "Report a problem" buttons across all
          tenants. Triage by setting status and recording notes — Eric activity
          rows referenced at report time are linked at the bottom of each
          detail.
        </p>
      </div>

      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Status:
          </span>
          {(['open', 'in_progress', 'resolved', 'dismissed', 'all'] as const).map(
            (s) => (
              <Button
                key={s}
                type="button"
                size="sm"
                variant={statusFilter === s ? 'default' : 'outline'}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'all' ? 'All' : STATUS_LABEL[s]}
              </Button>
            ),
          )}
          {query.isFetching ? (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          ) : null}
        </div>
      </Card>

      {query.isError ? (
        <Card className="border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : 'Failed to load alerts.'}
        </Card>
      ) : null}
      {query.isLoading ? (
        <Card className="p-4 text-sm text-muted-foreground">
          {tCommon('loading')}
        </Card>
      ) : null}

      {query.data && query.data.entries.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No alerts in this view. The page auto-refreshes every 15s.
        </Card>
      ) : null}

      {query.data && query.data.entries.length > 0 ? (
        <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
          <Card className="overflow-hidden p-0">
            <ul className="divide-y divide-border">
              {query.data.entries.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(entry.id)}
                    className={
                      selectedId === entry.id
                        ? 'block w-full bg-muted/60 px-4 py-3 text-left'
                        : 'block w-full px-4 py-3 text-left hover:bg-muted/40'
                    }
                  >
                    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                      <span
                        className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TINT[entry.status]}`}
                      >
                        {STATUS_LABEL[entry.status]}
                      </span>
                      <span title={formatDateTime(entry.created_at)}>
                        {formatRelative(entry.created_at)}
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-foreground">
                      {entry.description}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {entry.shop_name ?? entry.shop_id}
                      {entry.reporter_email
                        ? ` · ${entry.reporter_email}`
                        : ''}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          {selected ? (
            <AlertDetail
              key={selected.id}
              entry={selected}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ['admin', 'alerts'] })
              }}
            />
          ) : (
            <Card className="grid place-items-center p-8 text-sm text-muted-foreground">
              Select an alert to triage.
            </Card>
          )}
        </div>
      ) : null}
    </div>
  )
}

function AlertDetail({
  entry,
  onSaved,
}: {
  entry: AlertEntry
  onSaved: () => void
}) {
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const [notes, setNotes] = useState(entry.triage_notes ?? '')

  const patch = useMutation({
    mutationFn: async (changes: Partial<{ status: AlertStatus; triage_notes: string | null }>) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/admin/alerts/${entry.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(changes),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || 'Failed to update alert')
      }
      return res.json()
    },
    onSuccess: () => onSaved(),
  })

  return (
    <Card className="space-y-4 p-5">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TINT[entry.status]}`}
          >
            {STATUS_LABEL[entry.status]}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatDateTime(entry.created_at)}
          </span>
        </div>
        <h2 className="mt-2 text-base font-semibold text-foreground">
          {entry.shop_name ?? entry.shop_id}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Reporter: {entry.reporter_email ?? entry.reporter_clerk_user_id}
        </p>
        {entry.page_context ? (
          <p className="mt-0.5 text-xs text-muted-foreground">
            Page: <code className="font-mono">{entry.page_context}</code>
          </p>
        ) : null}
      </div>

      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Description
        </h3>
        <p className="mt-1 text-sm whitespace-pre-wrap text-foreground">
          {entry.description}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`triage-notes-${entry.id}`}>Triage notes</Label>
        <Textarea
          id={`triage-notes-${entry.id}`}
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={4000}
          placeholder="What did you find? Anything you fixed? Why was this dismissed?"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={patch.isPending}
            onClick={() => patch.mutate({ triage_notes: notes || null })}
          >
            Save notes
          </Button>
          {entry.status !== 'in_progress' ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={patch.isPending}
              onClick={() =>
                patch.mutate({
                  status: 'in_progress',
                  triage_notes: notes || null,
                })
              }
            >
              Mark in progress
            </Button>
          ) : null}
          {entry.status !== 'resolved' ? (
            <Button
              type="button"
              size="sm"
              disabled={patch.isPending}
              onClick={() =>
                patch.mutate({
                  status: 'resolved',
                  triage_notes: notes || null,
                })
              }
            >
              Resolve
            </Button>
          ) : null}
          {entry.status !== 'dismissed' ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={patch.isPending}
              onClick={() =>
                patch.mutate({
                  status: 'dismissed',
                  triage_notes: notes || null,
                })
              }
            >
              Dismiss
            </Button>
          ) : null}
          {entry.status === 'resolved' || entry.status === 'dismissed' ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={patch.isPending}
              onClick={() =>
                patch.mutate({ status: 'open', triage_notes: notes || null })
              }
            >
              Re-open
            </Button>
          ) : null}
        </div>
        {patch.isError ? (
          <p className="text-xs text-destructive">
            {patch.error instanceof Error
              ? patch.error.message
              : 'Failed to save.'}
          </p>
        ) : null}
      </div>

      {entry.resolved_at ? (
        <p className="text-xs text-muted-foreground">
          Resolved {formatDateTime(entry.resolved_at)}{' '}
          {entry.resolved_by_clerk_user_id
            ? `by ${entry.resolved_by_clerk_user_id}`
            : ''}
        </p>
      ) : null}

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Related Eric conversation
        </h3>
        {entry.related.length === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">
            None attached.
          </p>
        ) : (
          <ol className="mt-2 space-y-2">
            {entry.related.map((rel) => (
              <li
                key={rel.id}
                className="rounded-md border border-border bg-background p-2 text-xs"
              >
                <div className="flex items-center justify-between text-muted-foreground">
                  <span className="uppercase tracking-wide">
                    {rel.channel ?? '?'}
                  </span>
                  <span>
                    {rel.created_at ? formatRelative(rel.created_at) : '—'}
                  </span>
                </div>
                {rel.request_text ? (
                  <p className="mt-1 text-foreground">
                    <span className="font-semibold text-muted-foreground">
                      User:
                    </span>{' '}
                    {rel.request_text}
                  </p>
                ) : (
                  <p className="mt-1 italic text-muted-foreground">
                    Request not found (purged or cross-tenant).
                  </p>
                )}
                {rel.final_reply ? (
                  <p className="mt-0.5 text-foreground">
                    <span className="font-semibold text-muted-foreground">
                      Eric:
                    </span>{' '}
                    {rel.final_reply}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </div>
    </Card>
  )
}
